import axios, { AxiosResponse, CancelToken } from "axios"
import { documentoComercialValidacionAdapter } from "./adapter_globals/DocumentoComercialValidacion.adapter"
import { DocumentoComercialAsociadoReadingDtoProps } from "./DTOs/DocumentoComercialAsociadoReadingDto"
import { DocumentoComercialValidacionDtoProps } from "./DTOs/DocumentoComercialValidacionDto"
import { FicheroCreationDtoProps } from "./DTOs/FicheroCreationDto"
import { FicheroReadingDtoProps } from "./DTOs/FicheroReadingDto"
import { PresupuestoCreationDtoProps } from "./DTOs/PresupuestoCreationDto"
import { PresupuestoReadingDtoProps } from "./DTOs/PresupuestoReadingDto"
import { PresupuestoReadingFiltersDtoProps } from "./DTOs/PresupuestoReadingFiltersDto"
import { PresupuestoReadingSimpleDtoProps } from "./DTOs/PresupuestoReadingSimpleDto"
import { ValidacionDocumentoReadingDtoProps } from "./DTOs/ValidacionDocumentoReadingDto"
import { DocumentoComercialValidacionDtoQueryParamsProps } from "./interfaces/DocumentoComercialValidacionDtoQueryParamsProps"
import { SortingCriteria } from "./interfaces/SortingCriteria"
import { PresupuestoModelProps } from "./Models/PresupuestoModel"
import {
  presupuestoByFilterAdapter,
  presupuestoById,
  presupuestoByIdSimple,
  presupuestoDocAsociadosAdapter,
  presupuestoDocumentoPendienteValidacionAdapter,
  presupuestoFicherosAdjuntosAdapter,
} from "./Presupuesto.adapter"
import { PresupuestoReadingFiltersDtoIPagedEnumerableProps } from "./Schemas/PresupuestoReadingFiltersDtoIPagedEnumerable"
import { EstadoRevisionDocumentoComercialType } from "./Types/EstadoRevisionDocumentoComercial"
import { TipoDocumentoComercialType } from "./Types/TipoDocumentoComercial"

export interface ByFilterPresupuestoQueryParamsProps {
  EmailLibrado?: string
  EmpresaActivaIdUsuario?: string
  EstadoRevision?: EstadoRevisionDocumentoComercialType[]
  ClienteNif?: string
  ProveedorNif?: string
  FechaEmisionLowerLimit?: string
  FechaEmisionUpperLimit?: string
  ImporteLowerLimit?: number | string
  ImporteUpperLimit?: number | string
  LibradoId?: string
  LibradorId?: string
  MaxResultCount?: number | string
  MsgEncrypted?: string
  SkipCount?: number
  SortingCriteria: SortingCriteria
  Keyword?: string
}

export async function getPresupuestoByFilter(
  queryParams: ByFilterPresupuestoQueryParamsProps,
  cancelToken?: CancelToken
): Promise<PresupuestoReadingFiltersDtoIPagedEnumerableProps> {
  const { data }: { data: PresupuestoReadingFiltersDtoIPagedEnumerableProps } =
    await axios.get("/Presupuesto/by-filters", {
      params: queryParams,
      cancelToken,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((presupuesto: PresupuestoReadingFiltersDtoProps) =>
      presupuestoByFilterAdapter(presupuesto)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * @description Check if User has any Presupuesto
 * @returns {Boolean}
 */
export async function getUnsignedPresupuesto(
  queryParams?: DocumentoComercialValidacionDtoQueryParamsProps
): Promise<Array<DocumentoComercialValidacionDtoProps>> {
  const {
    data,
  }: Record<
    "data",
    Array<DocumentoComercialValidacionDtoProps>
  > = await axios.get("/Presupuesto/pendientes-firmar", {
    params: queryParams,
  })
  return data.map((documento: DocumentoComercialValidacionDtoProps) =>
    documentoComercialValidacionAdapter(documento)
  )
}

export async function postValidatePresupuestoPendiente(
  id: string
): Promise<void> {
  await axios.post(`/Presupuesto/${id}/validar`)
}

export async function getPresupuestoByIdSimple(
  id: string
): Promise<PresupuestoReadingSimpleDtoProps> {
  const { data } = await axios.get(`/Presupuesto/${id}/simple`)
  return presupuestoByIdSimple(data)
}

export async function getPresupuestoById(
  id: string
): Promise<PresupuestoReadingDtoProps> {
  const { data } = await axios.get(`/Presupuesto/${id}`)
  return presupuestoById(data)
}

export async function getPresupuestoPdfById(id: string) {
  const { data, status } = await axios.get(`/Presupuesto/${id}/pdf`, {
    responseType: "blob",
  })
  return { data, status }
}

export async function postPresupuesto(documento: PresupuestoCreationDtoProps) {
  const resp = await axios.post("/Presupuesto", documento)
  return resp
}

export async function postPresupuestoPdf(documento: PresupuestoModelProps) {
  const { data } = await axios.post("/Presupuesto/generate-pdf", documento, {
    responseType: "blob",
  })
  return data
}

export async function getPresupuestoLastAvailableNumero(
  serie: string
): Promise<number> {
  const response = await axios.get(
    `/Presupuesto/last-available-numero/${serie}`
  )
  return response.data
}

export async function getPresupuestoFicherosAdjuntosId(
  id: string
): Promise<FicheroReadingDtoProps[]> {
  const { data } = await axios.get(`/Presupuesto/${id}/ficheros-adjuntos`)
  return data.map((element: FicheroReadingDtoProps) =>
    presupuestoFicherosAdjuntosAdapter(element)
  )
}

export async function postPresupuestoGenerateExcel(queryParams: {
  tipoDocumentoComercial: TipoDocumentoComercialType
}): Promise<AxiosResponse<Blob>> {
  return axios.get("/Presupuesto/exportar-excel", {
    params: queryParams,
    responseType: "blob",
  })
}

export async function getPresupuestoDocsAsociadosById(
  id: string
): Promise<DocumentoComercialAsociadoReadingDtoProps[]> {
  const { data } = await axios.get(`/Presupuesto/${id}/documentos-asociados`)
  return data.map((element: DocumentoComercialAsociadoReadingDtoProps) =>
    presupuestoDocAsociadosAdapter(element)
  )
}

export async function getPresupuestoDocumentoPendienteValidacionDocumentoPendienteValidacion(
  id: string
): Promise<ValidacionDocumentoReadingDtoProps> {
  const { data } = await axios.get(`/Presupuesto/${id}/validacion`)
  return presupuestoDocumentoPendienteValidacionAdapter(data)
}

export async function deletePresupuestoArichivoAdjunto(
  presupuestoID: string,
  fileID: string
) {
  return axios.delete(`/Presupuesto/${presupuestoID}/fichero/${fileID}`)
}

export async function postPresupuestoAñadirArchivoAdjunto(
  presupuestoID: string,
  file: FicheroCreationDtoProps
) {
  return axios.post(`/Presupuesto/${presupuestoID}/add-fichero-adjunto`, file)
}
